import { ITEMS, type ItemId, isItemId } from '@/data/items';

/**
 * The backpack: storage, and nothing else.
 *
 * One flat list of slots, all equal. What is to *hand* is the bag's business
 * (see quickBag.ts), which points at items in here by id rather than holding
 * them. Storage used to double as the quick-access row — the first eight slots
 * were the bar — which meant arranging your backpack silently rearranged what
 * your thumb could reach, and there was no way to keep something stored without
 * it taking a bar slot.
 *
 * Capacity grows with upgrades, which is why slots are not a fixed-length
 * tuple.
 */

/** Slots a new game starts with, hotbar included. */
export const STARTING_CAPACITY = 12;

/** Ceiling, so an upgrade chain cannot produce an unusable grid. */
export const MAX_CAPACITY = 36;

/** Floor, so a corrupt capacity cannot produce a backpack too small to play. */
const MIN_CAPACITY = 8;

export interface Slot {
  readonly id: ItemId;
  count: number;
}

export interface SlotSave {
  readonly id: string;
  readonly count: number;
}

export class Inventory {
  private slots: (Slot | null)[];

  constructor(capacity: number = STARTING_CAPACITY) {
    this.slots = Array.from({ length: clampCapacity(capacity) }, () => null);
  }

  get capacity(): number {
    return this.slots.length;
  }

  /**
   * Add slots. Returns how many were actually added, which is zero once the
   * bag is at its ceiling — the caller needs to know so it does not charge the
   * player for nothing.
   */
  expand(by: number): number {
    const target = clampCapacity(this.slots.length + by);
    const added = target - this.slots.length;
    for (let i = 0; i < added; i++) this.slots.push(null);
    return added;
  }

  /** Swap two slots, for dragging something out of the bag into hand. */
  swap(a: number, b: number): boolean {
    if (!this.isSlot(a) || !this.isSlot(b) || a === b) return false;
    const held = this.slots[a] ?? null;
    this.slots[a] = this.slots[b] ?? null;
    this.slots[b] = held;
    return true;
  }

  /**
   * Move one slot onto another: same item stacks merge, anything else swaps.
   *
   * Merging matters because the bag fragments on its own — harvests land
   * wherever there is room — and a player tidying up expects two piles of
   * turnips dropped together to become one pile, not to trade places.
   * Returns false when nothing changed, so the caller can stay quiet.
   */
  mergeOrSwap(from: number, to: number): boolean {
    if (!this.isSlot(from) || !this.isSlot(to) || from === to) return false;

    const source = this.slots[from] ?? null;
    const target = this.slots[to] ?? null;
    if (!source && !target) return false;

    if (source && target && source.id === target.id) {
      const room = ITEMS[source.id].stackSize - target.count;
      // A full target is not a failed move, it is a swap: the player asked for
      // these two to change places and there is nowhere for the pile to go.
      if (room <= 0) return this.swap(from, to);

      const moved = Math.min(room, source.count);
      target.count += moved;
      source.count -= moved;
      if (source.count <= 0) this.slots[from] = null;
      return moved > 0;
    }

    return this.swap(from, to);
  }

  /**
   * Take a whole slot out, returning what was in it.
   *
   * A transfer, not a deletion: the caller is moving the stack somewhere the
   * player can reach it again, and is responsible for putting it there.
   */
  takeSlot(index: number): Slot | null {
    if (!this.isSlot(index)) return null;
    const slot = this.slots[index] ?? null;
    this.slots[index] = null;
    return slot;
  }

  private isSlot(index: number): boolean {
    return Number.isInteger(index) && index >= 0 && index < this.slots.length;
  }

  static starting(): Inventory {
    const inventory = new Inventory();
    // Tools live on the belt, not in storage.
    inventory.add('bait:worm', 15);
    inventory.add('seed:turnip', 8);
    inventory.add('feed', 12);
    // Deliberately leaves slots free. A starting kit that fills the bag means
    // the player's first harvest overflows, which is a miserable first lesson.
    return inventory;
  }

  slotAt(index: number): Slot | null {
    return this.slots[index] ?? null;
  }

  countOf(id: ItemId): number {
    let total = 0;
    for (const slot of this.slots) {
      if (slot?.id === id) total += slot.count;
    }
    return total;
  }

  /**
   * Add items, filling existing stacks first.
   * Returns how many did not fit, so a full hotbar is visible rather than
   * silently eating a harvest.
   */
  add(id: ItemId, count: number): number {
    if (!isItemId(id) || count <= 0) return count;
    const stackSize = ITEMS[id].stackSize;
    let remaining = count;

    for (const slot of this.slots) {
      if (remaining === 0) break;
      if (slot?.id !== id) continue;
      const space = stackSize - slot.count;
      const moved = Math.min(space, remaining);
      slot.count += moved;
      remaining -= moved;
    }

    for (let i = 0; i < this.slots.length && remaining > 0; i++) {
      if (this.slots[i]) continue;
      const moved = Math.min(stackSize, remaining);
      this.slots[i] = { id, count: moved };
      remaining -= moved;
    }

    return remaining;
  }

  /** Stack limit for an item, so callers do not have to reach into ITEMS. */
  stackSizeOf(id: ItemId): number {
    return ITEMS[id].stackSize;
  }

  /** Could `count` of this item be added right now? */
  hasRoomFor(id: ItemId, count: number): boolean {
    const stackSize = ITEMS[id].stackSize;
    let space = 0;
    for (const slot of this.slots) {
      if (slot === null) space += stackSize;
      else if (slot.id === id) space += stackSize - slot.count;
      if (space >= count) return true;
    }
    return space >= count;
  }

  /** Take items from anywhere in the bag. Returns how many were actually removed. */
  remove(id: ItemId, count: number): number {
    let remaining = count;
    for (let i = 0; i < this.slots.length && remaining > 0; i++) {
      const slot = this.slots[i];
      if (slot?.id !== id) continue;
      const taken = Math.min(slot.count, remaining);
      slot.count -= taken;
      remaining -= taken;
      if (slot.count <= 0) this.slots[i] = null;
    }
    return count - remaining;
  }

  toSave(): (SlotSave | null)[] {
    return this.slots.map((slot) => (slot ? { id: slot.id, count: slot.count } : null));
  }

  static fromSave(entries: unknown): Inventory {
    if (!Array.isArray(entries)) return Inventory.starting();

    // Capacity is recovered from the saved length, so an upgraded bag stays
    // upgraded without a second field to keep in sync.
    const inventory = new Inventory(Math.max(STARTING_CAPACITY, entries.length));

    for (let i = 0; i < Math.min(inventory.capacity, entries.length); i++) {
      const entry = entries[i] as SlotSave | null;
      if (!entry || !isItemId(entry.id)) continue;
      const count = Math.floor(entry.count);
      if (!Number.isFinite(count) || count <= 0) continue;
      inventory.slots[i] = { id: entry.id, count: Math.min(count, ITEMS[entry.id].stackSize) };
    }

    return inventory;
  }
}

function clampCapacity(value: number): number {
  if (!Number.isFinite(value)) return STARTING_CAPACITY;
  return Math.min(MAX_CAPACITY, Math.max(MIN_CAPACITY, Math.floor(value)));
}
